import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { StreamChat } from "stream-chat";
import { toast } from "react-toastify";
import { getStreamChatTokenAPI } from "../../api";
import { CHAT_FULL_FEATURES } from "./chatConfig";
import {
  setActiveStaffChatClient,
  getActiveStaffChatClient,
  disconnectStaffChat,
} from "./staffChatClient";

// status: idle | connecting | ready | unavailable | disabled
const defaultValue = {
  client: null,
  ready: false,
  status: "idle",
  unreadCount: 0,
  currentUserId: null,
  currentUserName: "",
  team: null,
  // Super Admin only: can open the chats of the other staff (read only)
  canViewAllChats: false,
  // false = group chat only (REACT_APP_AQATO_ADMIN_PORTAL_CHAT_FULL_FEATURES in .env)
  fullFeatures: CHAT_FULL_FEATURES,
};

const StaffChatContext = createContext(defaultValue);

export const useStaffChat = () => useContext(StaffChatContext);

// Attachments: images and common documents only (no video)
const ACCEPTED_FILES = [
  "image/*",
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".csv",
  ".txt",
  ".ppt",
  ".pptx",
];
const ACCEPTED_EXTENSIONS = ACCEPTED_FILES.filter((item) =>
  item.startsWith(".")
);

const isAllowedChatFile = (attachment) => {
  const file = attachment?.localMetadata?.file;
  const mimeType = file?.type || attachment?.mime_type || "";
  const fileName = (file?.name || attachment?.title || "").toLowerCase();

  const allowed =
    mimeType.startsWith("image/") ||
    ACCEPTED_EXTENSIONS.some((extension) => fileName.endsWith(extension));

  if (!allowed) {
    toast.error(
      "Only images and documents (PDF, Word, Excel, PowerPoint, CSV, TXT) can be sent in chat.",
      { toastId: "staff-chat-file-not-allowed" }
    );
  }
  return allowed;
};

const CHAT_PAGE_PATH = "/admin/chat";

const isMutedForUser = (client, cid, senderId) => {
  const channelMuted = (client.mutedChannels || []).some(
    (mute) => mute.channel?.cid === cid
  );
  const userMuted = (client.mutedUsers || []).some(
    (mute) => mute.target?.id === senderId
  );
  return channelMuted || userMuted;
};

// Toast for a new message, when the user is not on the Chat page
const showNewMessageToast = (client, event, openChat) => {
  const message = event.message;
  if (!message || !message.id) {
    return;
  }
  // Only normal messages and replies (not the "X added Y" lines)
  if (message.type && message.type !== "regular" && message.type !== "reply") {
    return;
  }

  const senderId = message.user?.id;
  if (!senderId || senderId === client.userID) {
    return;
  }
  if (window.location.pathname === CHAT_PAGE_PATH) {
    return;
  }

  const cid = event.cid || event.channel?.cid || message.cid || "";
  if (isMutedForUser(client, cid, senderId)) {
    return;
  }

  // Super Admin: no toast for the chats of other staff which he only opened to look at
  const watchedChannel = client.activeChannels?.[cid];
  if (
    watchedChannel &&
    !watchedChannel.state?.membership?.user &&
    !watchedChannel.state?.members?.[client.userID]
  ) {
    return;
  }

  // Group chat only mode: the 1-1 chats cannot be opened, so no toast for them
  if (!CHAT_FULL_FEATURES) {
    const isGroupChat =
      event.channel?.is_group === true ||
      watchedChannel?.data?.is_group === true;
    if (!isGroupChat) {
      return;
    }
  }

  const senderName = message.user?.name || "New message";
  const groupName =
    event.channel?.name || client.activeChannels?.[cid]?.data?.name || "";

  let text = (message.text || "").trim();
  if (!text) {
    text = message.attachments?.length ? "Sent an attachment" : "Sent a message";
  }
  if (text.length > 80) {
    text = text.slice(0, 80) + "...";
  }

  const channelId = event.channel_id || cid.split(":")[1] || "";

  toast.info(
    <div style={{ cursor: "pointer" }}>
      <strong>{senderName}</strong>
      {groupName ? ` in ${groupName}` : ""}
      <div>{text}</div>
    </div>,
    {
      // Same message is never shown twice
      toastId: `staff-chat-message-${message.id}`,
      onClick: () => openChat(channelId),
    }
  );
};

const fetchChatTokenData = async () => {
  const response = await getStreamChatTokenAPI();

  if (
    response &&
    response?.data &&
    response?.data?.response &&
    response?.data?.response?.response === true &&
    response?.data?.response?.data
  ) {
    return response.data.response.data;
  }

  throw new Error(
    response?.data?.response?.error_msg || "Chat is not available"
  );
};

const StaffChatProvider = ({ loggedIn, children }) => {
  const [value, setValue] = useState(defaultValue);

  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // Connect after login, disconnect after logout
  useEffect(() => {
    if (!loggedIn) {
      return;
    }

    // Read only accounts do not have chat
    const accountType = localStorage.getItem("AQATO_SuperAdminAccountType");
    if (accountType === "aqato_read_only") {
      setValue({ ...defaultValue, status: "disabled" });
      return () => {
        setValue(defaultValue);
      };
    }

    setValue({ ...defaultValue, status: "connecting" });

    let cancelled = false;
    let client = null;
    let unreadListener = null;

    const connect = async () => {
      try {
        const tokenData = await fetchChatTokenData();
        if (cancelled) {
          return;
        }

        const apiKey =
          tokenData.api_key ||
          process.env.REACT_APP_AQATO_ADMIN_PORTAL_STREAM_API_KEY;

        client = StreamChat.getInstance(apiKey);
        setActiveStaffChatClient(client);

        client.setMessageComposerSetupFunction(({ composer }) => {
          composer.updateConfig({
            attachments: {
              acceptedFiles: ACCEPTED_FILES,
              fileUploadFilter: isAllowedChatFile,
            },
          });
        });

        // The first token is already here. When it expires, Stream asks for a new one from the backend.
        let firstToken = tokenData.user_token;
        const tokenProvider = async () => {
          if (firstToken) {
            const token = firstToken;
            firstToken = null;
            return token;
          }
          const freshTokenData = await fetchChatTokenData();
          return freshTokenData.user_token;
        };

        const connection = await client.connectUser(
          { id: tokenData.user_id, name: tokenData.user_name },
          tokenProvider
        );
        if (cancelled) {
          return;
        }

        unreadListener = client.on((event) => {
          if (event.total_unread_count !== undefined) {
            setValue((previous) =>
              previous.unreadCount === event.total_unread_count
                ? previous
                : { ...previous, unreadCount: event.total_unread_count }
            );
          }

          if (
            event.type === "message.new" ||
            event.type === "notification.message_new"
          ) {
            showNewMessageToast(client, event, (channelId) => {
              navigateRef.current(
                channelId
                  ? `${CHAT_PAGE_PATH}?channel=${encodeURIComponent(channelId)}`
                  : CHAT_PAGE_PATH
              );
            });
          }
        });

        setValue({
          client: client,
          ready: true,
          status: "ready",
          unreadCount:
            connection?.me?.total_unread_count ||
            client.user?.total_unread_count ||
            0,
          currentUserId: tokenData.user_id,
          currentUserName: tokenData.user_name,
          team: tokenData.team,
          canViewAllChats:
            CHAT_FULL_FEATURES && tokenData.can_view_all_chats === true,
          fullFeatures: CHAT_FULL_FEATURES,
        });
      } catch (error) {
        // The rest of the portal keeps working without chat
        console.error("Staff chat is not connected:", error);
        if (!cancelled) {
          setValue({ ...defaultValue, status: "unavailable" });
        }
      }
    };

    const connecting = connect();

    return () => {
      cancelled = true;
      setValue(defaultValue);

      connecting.finally(() => {
        if (unreadListener) {
          unreadListener.unsubscribe();
        }
        if (client) {
          if (getActiveStaffChatClient() === client) {
            setActiveStaffChatClient(null);
          }
          client.disconnectUser().catch((error) => {
            console.error("Error disconnecting chat:", error);
          });
        }
      });
    };
  }, [loggedIn]);

  // Logout done in another tab
  useEffect(() => {
    if (!loggedIn) {
      return;
    }

    const channel = new BroadcastChannel("user-status-channel");

    channel.onmessage = (eventMessage) => {
      if (eventMessage?.data?.type === "logout") {
        disconnectStaffChat();
        setValue(defaultValue);
      }
    };

    return () => {
      channel.close();
    };
  }, [loggedIn]);

  return (
    <StaffChatContext.Provider value={value}>
      {children}
    </StaffChatContext.Provider>
  );
};

export default StaffChatProvider;
